import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component
} from '@angular/core';
import {DetectMethodChanges, isVoid} from 'projects/shared-components/utils';
import {TradingInstrumentsService} from 'projects/shared-components/trading-instruments/trading-instruments-service.interface';
import {TradingInstrument} from 'projects/shared-components/trading-instruments/trading-instrument.class';
import {ToastrService} from 'ngx-toastr';

export interface HgCreatePortfolioResult {
    name: string;
    underlying: string;
    defaultQty: number;
}

/**
 * Creation dialog for INDEPENDENT Hedging Grid portfolios (CR-20260606-001).
 * Inputs: name (required), underlying via <ets-symbol-picker> (required, immutable after
 * creation), default qty. Promise-popover pattern: show() resolves with the result, or
 * null if dismissed.
 */
@Component({
    selector: 'ets-hg-create-portfolio-dialog',
    templateUrl: 'hg-create-portfolio-dialog.component.html',
    styleUrls: ['hg-create-portfolio-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HgCreatePortfolioDialogComponent {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _tiService: TradingInstrumentsService,
        private readonly _toastr: ToastrService
    ) {
    }

    readonly window = window;

    visible = false;

    name: string;

    tradingInstrument: TradingInstrument;

    defaultQty = 1;

    private _resolver: (value: HgCreatePortfolioResult | null) => void;

    @DetectMethodChanges()
    show(): Promise<HgCreatePortfolioResult | null> {
        this.name = null;
        // Default the underlying to SPX (owner default); user can change it before creating.
        this.tradingInstrument = this._tiService.getInstrumentByTicker('SPX');
        this.defaultQty = 1;
        this.visible = true;

        setTimeout(() => this._changeDetector.detectChanges());

        return new Promise<HgCreatePortfolioResult | null>((resolve) => {
            this._resolver = resolve;
        });
    }

    @DetectMethodChanges()
    onInstrumentSelected(instrument: TradingInstrument) {
        this.tradingInstrument = instrument;
    }

    @DetectMethodChanges()
    onCreate() {
        if (isVoid(this.name)) {
            this._toastr.error('Please provide a portfolio name');
            return;
        }

        if (isVoid(this.tradingInstrument)) {
            this._toastr.error('Please select an underlying');
            return;
        }

        const qty = this.defaultQty;
        if (isVoid(qty) || qty < 1) {
            this._toastr.error('Default quantity must be a positive number');
            return;
        }

        this.resolveWith({
            name: this.name.trim(),
            underlying: this.tradingInstrument.ticker,
            defaultQty: qty
        });
    }

    @DetectMethodChanges()
    onCancel() {
        this.resolveWith(null);
    }

    @DetectMethodChanges()
    onHidden() {
        // Popup dismissed without an explicit action — treat as cancel.
        this.resolveWith(null);
    }

    private resolveWith(result: HgCreatePortfolioResult | null) {
        if (this._resolver) {
            this._resolver(result);
            this._resolver = null;
        }
        this.visible = false;
    }
}
